import { useMemo, useContext } from "react";

// Constants
import { DATA_LOADED, INSTANCE_TYPE_ORACLE, INSTANCE_TYPE_SQLSERVER } from "../../Constants";

// Components
import WaitsTable from "./WaitsTable";
import DatabasesTable from "./DatabasesTable";
import SessionsTable from "./SessionsTable";
import ClientsTable from "./ClientsTable";
import UsersTable from "./UsersTable";
import SchemasTable from "./SchemasTable";
import OsUsersTable from "./OsUsersTable";
import ProgramsTable from "./ProgramsTable";
import StatementsTable from "./StatementsTable";
import Batches from "../Tabs/Batches";

// Types
import InstanceTarget from "../../../types/instance/InstanceTarget";

// Contexts
import { FilterContext } from "../../../context/FilterContext";

// Helpers
import { getFilterParameters } from "../../../helpers/utils";
import WaitsDataTable from "../../../types/instance/tables/WaitsDataTable";
import ConditionalRender from "../../../helpers/ConditionalRender";
import GroupedStatementsTable from "./GroupedStatementsTable";
import GroupedBatches from "../Tabs/GroupedBatches";
import ClientsDataTable from "../../../types/instance/tables/ClientsDataTable";
import DatabasesDataTable from "../../../types/instance/tables/DatabasesDataTable";
import SessionsDataTable from "../../../types/instance/tables/SessionsDataTable";
import ProgramsDataTable from "../../../types/instance/tables/ProgramsDataTable";
import UsersDataTable from "../../../types/instance/tables/UsersDataTable";
import SchemasDataTable from "../../../types/instance/tables/SchemasDataTable";
import OsUsersDataTable from "../../../types/instance/tables/OsUsersDataTable";

export default function TablesWrapper(props: {
    instance: InstanceTarget,
    loading: number,
    totalInstanceTime: number,
    statementsDataTable: any,
    waitsDataTable: WaitsDataTable[],
    clientsDataTable: ClientsDataTable[],
    databasesDataTable: DatabasesDataTable[],
    sessionsDataTable: SessionsDataTable[],
    programsDataTable: ProgramsDataTable[],
    usersDataTable: UsersDataTable[],
    schemasDataTable: SchemasDataTable[],
    osUsersDataTable: OsUsersDataTable[],
    currentTab: string,
    setFilterOptions: Function,
    setBatchesOptions: Function,
    isGroup: boolean,
    onStatementClick?: (statementId: string, statementType?: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch') => void
}) {
    const {
        instance,
        loading,
        currentTab,
        totalInstanceTime,
        statementsDataTable,
        waitsDataTable,
        setFilterOptions,
        setBatchesOptions,
        isGroup
    } = props

    const { currentFilterOptions } = useContext(FilterContext);
    const filterParameters = getFilterParameters(currentFilterOptions);

    const tableTabsComponent = useMemo(() => (
            <div className="tab-content">
                <ConditionalRender if={isGroup}>
                    <GroupedStatementsTable loading={loading} filtersParameters={filterParameters}
                                            instance={instance}
                                            currentTab={currentTab}
                                            instanceTime={totalInstanceTime} waits={waitsDataTable} showByDefault={currentTab === '' || currentTab === 'Gr Statements'}
                                            statementsDataTable={statementsDataTable}
                                            showGroupedViewToggle
                                            onStatementClick={props.onStatementClick}/>
                    {instance.type === INSTANCE_TYPE_SQLSERVER && loading === DATA_LOADED && (
                            <GroupedBatches instanceId={instance.id}
                                            currentTab={currentTab}
                                            filtersParameters={filterParameters}
                                            totalInstanceTime={totalInstanceTime} waits={waitsDataTable} showByDefault={currentTab === 'Gr Batches'}
                                            isBatchesForStatement={false} setBatchesOptions={setBatchesOptions}
                                            showGroupedViewToggle
                                            onStatementClick={props.onStatementClick}/>
                    )}
                </ConditionalRender>
                <ConditionalRender if={!isGroup}>
                    <StatementsTable loading={loading} instance={instance}
                                     instanceTime={totalInstanceTime} waits={waitsDataTable} showByDefault={currentTab === '' || currentTab === 'Statements'}
                                     currentTab={currentTab}
                                     statementsDataTable={statementsDataTable}
                                     showGroupedViewToggle
                                     onStatementClick={props.onStatementClick}/>
                    {instance.type === INSTANCE_TYPE_SQLSERVER && loading === DATA_LOADED && (
                            <Batches instanceId={instance.id}
                                     filtersParameters={filterParameters}
                                     currentTab={currentTab}
                                     totalInstanceTime={totalInstanceTime} waits={waitsDataTable} showByDefault={currentTab === 'Batches'}
                                     isBatchesForStatement={false} setBatchesOptions={setBatchesOptions}
                                            showGroupedViewToggle
                                     onStatementClick={props.onStatementClick}/>
                    )}
                </ConditionalRender>
                <WaitsTable loading={loading} instance={instance}
                            instanceTime={totalInstanceTime} isDefault={false}
                            currentTab={currentTab}
                            waitsDataTable={waitsDataTable} setFilterOptions={setFilterOptions}/>
                <DatabasesTable instance={instance}
                                instanceTime={totalInstanceTime}
                                databasesDataTable={props.databasesDataTable}
                                currentTab={currentTab}
                                setFilterOptions={setFilterOptions}/>
                {instance.type === INSTANCE_TYPE_ORACLE && (
                        <SchemasTable instanceId={instance.id}
                                      instanceTime={totalInstanceTime}
                                      currentTab={currentTab}
                                      schemasDataTable={props.schemasDataTable}
                                      setFilterOptions={setFilterOptions}/>
                )}
                <SessionsTable instanceId={instance.id}
                               instanceTime={totalInstanceTime}
                               sessionsDataTable={props.sessionsDataTable}
                               currentTab={currentTab}
                               key='sessions'
                               setFilterOptions={setFilterOptions}/>
                <ClientsTable instanceId={instance.id}
                              instanceTime={totalInstanceTime}
                              clientsDataTable={props.clientsDataTable}
                              currentTab={currentTab}
                              setFilterOptions={setFilterOptions}/>
                <UsersTable instanceId={instance.id}
                            instanceTime={totalInstanceTime}
                            currentTab={currentTab}
                            usersDataTable={props.usersDataTable} setFilterOptions={setFilterOptions}/>
                {instance.type === INSTANCE_TYPE_ORACLE && (
                        <OsUsersTable instanceId={instance.id}
                                      instanceTime={totalInstanceTime}
                                      currentTab={currentTab}
                                      osUsersDataTable={props.osUsersDataTable}
                                      setFilterOptions={setFilterOptions}/>
                )}
                <ProgramsTable instanceId={instance.id}
                               instanceTime={totalInstanceTime}
                               currentTab={currentTab}
                               programsDataTable={props.programsDataTable}
                               setFilterOptions={setFilterOptions}/>
            </div>
    ), [filterParameters,
        instance,
        loading,
        totalInstanceTime,
        statementsDataTable,
        waitsDataTable,
        setFilterOptions,
        setBatchesOptions,
        currentTab,
        props.schemasDataTable,
        props.osUsersDataTable]);
    return (<div>{tableTabsComponent}</div>
    )
}
